import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetStripeTerms } from '@/components/OutletPaymentsSection/useOutletPayments';

/**
 * TREŚĆ REGULAMINU PŁATNOŚCI ONLINE WRAZ Z ZAŁĄCZNIKIEM RODO.
 *
 * TO JEST DOKUMENT ZAPASOWY. Obowiązującą wersję publikuje HotelSpot z hs-admin i to ona
 * jest pokazywana, gdy tylko istnieje (`v3-getStripeTerms`). Tekst poniżej odpowiada
 * `STRIPE_TERMS_VERSION` z backendu i obowiązuje na środowisku, na którym nic jeszcze nie
 * opublikowano.
 *
 * DOKUMENT WYMAGA PRZEGLĄDU PRAWNEGO PRZED URUCHOMIENIEM TRYBU LIVE. Odzwierciedla to,
 * co system faktycznie robi (direct charges na koncie hotelu, prowizja jako opłata
 * platformy, zwroty z salda hotelu), i jest napisany pod ochronę HotelSpot, ale nie
 * zastępuje opinii kancelarii.
 *
 * KAŻDA ZMIANA TREŚCI MUSI IŚĆ RAZEM Z PODBICIEM `STRIPE_TERMS_VERSION` w backendzie
 * (`functions-payments/src/core/types.ts`). Inaczej hotele, które zaakceptowały starsze
 * brzmienie, nie zostaną poproszone o zgodę na nowe, a zapis „kto i na co się zgodził"
 * przestanie odpowiadać rzeczywistości.
 *
 * DO UZUPEŁNIENIA PRZED LIVE: dane rejestrowe operatora (`OPERATOR`) oraz imienna lista
 * podprocesorów w Załączniku nr 1 pkt 6, jeżeli kancelaria uzna listę rodzajową za
 * niewystarczającą.
 */
const OPERATOR = 'HotelSpot [nazwa spółki], [adres siedziby], KRS [numer], NIP [numer]';

interface TermsSection {
  title: string;
  paragraphs: string[];
}

const TERMS_DOCUMENT: TermsSection[] = [
  {
    title: '§ 1. Definicje',
    paragraphs: [
      `HotelSpot: ${OPERATOR}, operator aplikacji HotelSpot, dalej także Operator.`,
      'Obiekt: przedsiębiorca prowadzący hotel lub inny obiekt, który korzysta z Aplikacji '
      + 'i włącza w niej płatności online.',
      'Aplikacja: oprogramowanie HotelSpot, w tym panel Obiektu i aplikacja gościa.',
      'Gość: osoba, która składa zamówienie w aplikacji gościa udostępnionej przez Obiekt.',
      'Stripe: Stripe Payments Europe, Limited z siedzibą w Irlandii oraz podmioty z nią powiązane.',
      'Konto Stripe: konto płatnicze prowadzone przez Stripe na rzecz Obiektu, podłączone do Aplikacji.',
      'Prowizja: wynagrodzenie HotelSpot, o którym mowa w § 6.',
    ],
  },
  {
    title: '§ 2. Rola HotelSpot',
    paragraphs: [
      '1. HotelSpot dostarcza wyłącznie oprogramowanie, które pozwala Obiektowi przyjmować '
      + 'płatności na jego własnym Koncie Stripe.',
      '2. HotelSpot nie jest dostawcą usług płatniczych w rozumieniu ustawy o usługach płatniczych. '
      + 'Nie przyjmuje, nie przechowuje ani nie przekazuje środków Gości. Środki z płatności trafiają '
      + 'bezpośrednio na Konto Stripe Obiektu i w żadnym momencie nie znajdują się w dyspozycji HotelSpot.',
      '3. HotelSpot nie jest stroną umowy sprzedaży zawieranej między Obiektem a Gościem ani stroną '
      + 'umowy między Obiektem a Stripe.',
      '4. HotelSpot nie jest agentem rozliczeniowym, pośrednikiem ani powiernikiem środków.',
    ],
  },
  {
    title: '§ 3. Konto Stripe',
    paragraphs: [
      '1. Włączenie płatności online wymaga podłączenia Konta Stripe. Umowę o prowadzenie konta '
      + 'Obiekt zawiera bezpośrednio ze Stripe i jest związany warunkami Stripe, w tym Stripe Services '
      + 'Agreement oraz Stripe Connected Account Agreement.',
      '2. Weryfikacja tożsamości oraz obowiązki wynikające z przepisów o przeciwdziałaniu praniu '
      + 'pieniędzy spoczywają na Obiekcie wobec Stripe. HotelSpot nie ma wpływu na wynik tej weryfikacji.',
      '3. Opłaty Stripe obciążają Obiekt. Opłata pobrana od płatności nie jest zwracana przez Stripe '
      + 'w razie zwrotu tej płatności Gościowi.',
      '4. Stripe może wstrzymać przyjmowanie płatności, wstrzymać wypłaty lub zamknąć Konto Stripe. '
      + 'HotelSpot nie odpowiada za takie decyzje ani za ich skutki, w tym za czasowy brak dostępności '
      + 'płatności online w Aplikacji.',
      '5. Cofnięcie autoryzacji Konta Stripe powoduje wyłączenie płatności online we wszystkich punktach '
      + 'sprzedaży Obiektu korzystających z tego konta.',
    ],
  },
  {
    title: '§ 4. Obiekt jako sprzedawca',
    paragraphs: [
      '1. Wobec Gościa sprzedawcą jest wyłącznie Obiekt.',
      '2. Obiekt odpowiada w szczególności za: treść oferty oraz ceny wraz z podatkami, wykonanie '
      + 'obowiązków informacyjnych wobec konsumenta, w tym informacji o prawie odstąpienia od umowy '
      + 'albo o jego wyłączeniu, udostępnienie Gościom własnego regulaminu i polityki prywatności, '
      + 'realizację zamówień, wystawienie paragonu lub faktury oraz rozliczenie podatków, w tym '
      + 'ewidencję sprzedaży przy zastosowaniu kasy rejestrującej.',
      '3. HotelSpot udostępnia wyłącznie narzędzia. Ustalenie ceny, opisu, dostępności oraz zasad '
      + 'anulowania i zwrotów należy do Obiektu.',
    ],
  },
  {
    title: '§ 5. Zwroty, reklamacje i obciążenia zwrotne',
    paragraphs: [
      '1. Zwrotu płatności dokonuje Obiekt ze swojego Konta Stripe. HotelSpot nie zwraca Gościom '
      + 'środków i nie ocenia zasadności zwrotu.',
      '2. Reklamacje Gości rozpatruje Obiekt, w terminach wynikających z przepisów.',
      '3. Obciążenia zwrotne, w tym chargeback i spory kartowe, obciążają Konto Stripe Obiektu wraz '
      + 'z opłatami Stripe. Postępowanie sporne Obiekt prowadzi samodzielnie. HotelSpot udostępnia '
      + 'na żądanie dane transakcji potrzebne do obrony.',
      '4. W razie zwrotu płatności Gościowi HotelSpot zwraca Prowizję proporcjonalnie do zwróconej kwoty.',
    ],
  },
  {
    title: '§ 6. Prowizja',
    paragraphs: [
      '1. Za udostępnienie funkcji płatności online HotelSpot pobiera Prowizję, liczoną procentowo '
      + 'od kwoty każdej udanej płatności.',
      '2. Obowiązująca stawka jest widoczna w panelu Obiektu, w ustawieniach punktu sprzedaży. '
      + 'Stawka może być różna dla różnych rodzajów punktów sprzedaży.',
      '3. Prowizja jest potrącana automatycznie w chwili płatności, przy użyciu mechanizmu opłaty '
      + 'platformy Stripe. Pobrana kwota zawiera podatek od towarów i usług. HotelSpot wystawia '
      + 'dokument rozliczeniowy obejmujący okresy miesięczne.',
      '4. O zmianie stawki HotelSpot informuje w panelu lub pocztą elektroniczną co najmniej 14 dni '
      + 'przed jej wejściem w życie. Do tego dnia obowiązuje stawka dotychczasowa. Obiekt, który nie '
      + 'akceptuje nowej stawki, może przed tą datą wyłączyć płatności online.',
    ],
  },
  {
    title: '§ 7. Obowiązki Obiektu',
    paragraphs: [
      'Obiekt zobowiązuje się do: utrzymywania aktualnych danych rejestrowych i kontaktowych, w tym '
      + 'danych obsługi Gości widocznych na potwierdzeniach płatności; prowadzenia sprzedaży zgodnie '
      + 'z prawem oraz z listą działalności zabronionych przez Stripe; niezwłocznego informowania '
      + 'HotelSpot o utracie kontroli nad Kontem Stripe, o zmianie podmiotu prowadzącego Obiekt oraz '
      + 'o żądaniach organów dotyczących płatności; zapewnienia, że osoba akceptująca Regulamin jest '
      + 'umocowana do reprezentowania Obiektu.',
    ],
  },
  {
    title: '§ 8. Wstrzymanie płatności',
    paragraphs: [
      '1. HotelSpot może wyłączyć płatności online dla Obiektu, w całości lub w części, gdy Obiekt '
      + 'narusza Regulamin lub przepisy prawa, zachodzi uzasadnione podejrzenie oszustwa albo prania '
      + 'pieniędzy, żąda tego Stripe, organ państwowy lub organizacja płatnicza, albo Obiekt zalega '
      + 'z zapłatą wynagrodzenia na rzecz HotelSpot.',
      '2. Wyłączenie płatności online nie pozbawia Obiektu dostępu do środków rozliczonych na jego '
      + 'Koncie Stripe.',
    ],
  },
  {
    title: '§ 9. Odpowiedzialność',
    paragraphs: [
      '1. HotelSpot dokłada starań o dostępność Aplikacji, lecz nie gwarantuje jej nieprzerwanego '
      + 'działania. Realizacja płatności zależy od Stripe, banków i dostawców metod płatności.',
      '2. HotelSpot nie odpowiada za skutki decyzji Stripe, za nieudane lub opóźnione płatności '
      + 'z przyczyn leżących po stronie dostawców płatności, za treść oferty Obiektu i sposób wykonania '
      + 'zamówień, za rozliczenia podatkowe Obiektu ani za roszczenia Gości kierowane wobec Obiektu.',
      '3. Odpowiedzialność HotelSpot wobec Obiektu ogranicza się do szkody rzeczywistej i do wysokości '
      + 'Prowizji pobranej od tego Obiektu w okresie 12 miesięcy poprzedzających zdarzenie wyrządzające '
      + 'szkodę. Odpowiedzialność za utracone korzyści jest wyłączona. Ograniczenia te nie mają '
      + 'zastosowania do szkody wyrządzonej umyślnie ani w zakresie, w jakim przepisy bezwzględnie '
      + 'obowiązujące nie pozwalają na ograniczenie odpowiedzialności.',
      '4. Obiekt zwolni HotelSpot z odpowiedzialności i pokryje uzasadnione koszty, w tym koszty obsługi '
      + 'prawnej, związane z roszczeniami Gości, organów, Stripe lub organizacji płatniczych, '
      + 'wynikającymi ze sprzedaży prowadzonej przez Obiekt albo z naruszenia Regulaminu przez Obiekt.',
    ],
  },
  {
    title: '§ 10. Dane osobowe',
    paragraphs: [
      '1. W zakresie danych osobowych Gości przetwarzanych w Aplikacji administratorem jest Obiekt, '
      + 'a HotelSpot przetwarza te dane na jego polecenie. Zasady określa Załącznik nr 1, który stanowi '
      + 'umowę powierzenia przetwarzania danych osobowych w rozumieniu art. 28 RODO. Akceptacja '
      + 'Regulaminu jest równoznaczna z zawarciem tej umowy.',
      '2. Stripe przetwarza dane związane z płatnością jako odrębny administrator, na podstawie własnej '
      + 'umowy z Obiektem i własnej polityki prywatności.',
      '3. Jeżeli Strony zawarły odrębną umowę powierzenia obejmującą całość Aplikacji, stosuje się ją. '
      + 'Załącznik nr 1 uzupełnia ją w zakresie płatności online i jej nie zastępuje.',
    ],
  },
  {
    title: '§ 11. Zmiany Regulaminu',
    paragraphs: [
      '1. HotelSpot może zmienić Regulamin z ważnych przyczyn, w szczególności w razie zmiany przepisów, '
      + 'zmiany warunków Stripe, zmiany zakresu funkcji Aplikacji albo zmiany zasad wynagrodzenia.',
      '2. Nowa wersja jest przedstawiana w panelu i wymaga ponownej akceptacji. Do czasu akceptacji '
      + 'obowiązuje wersja zaakceptowana wcześniej, przy czym HotelSpot może wyłączyć płatności online, '
      + 'jeżeli akceptacja nie nastąpi w terminie 30 dni od przedstawienia nowej wersji.',
      '3. Każda wersja ma identyfikator widoczny pod treścią. HotelSpot zapisuje, kto i kiedy '
      + 'zaakceptował daną wersję.',
    ],
  },
  {
    title: '§ 12. Postanowienia końcowe',
    paragraphs: [
      '1. Regulamin dotyczy wyłącznie relacji między przedsiębiorcami.',
      '2. Prawem właściwym jest prawo polskie, a sądem właściwym sąd miejscowo właściwy dla siedziby '
      + 'HotelSpot.',
      '3. Wiążąca jest polska wersja językowa Regulaminu.',
      '4. Nieważność albo bezskuteczność pojedynczego postanowienia nie wpływa na pozostałe.',
    ],
  },
  {
    title: 'Załącznik nr 1. Umowa powierzenia przetwarzania danych osobowych',
    paragraphs: [
      '1. Przedmiot, charakter i cel. Obiekt jako administrator powierza HotelSpot jako podmiotowi '
      + 'przetwarzającemu przetwarzanie danych osobowych w celu świadczenia funkcji Aplikacji, w tym '
      + 'przyjmowania i obsługi zamówień oraz płatności online. Przetwarzanie ma charakter ciągły '
      + 'i trwa przez okres korzystania przez Obiekt z Aplikacji.',
      '2. Rodzaj danych i kategorie osób. Powierzenie obejmuje dane Gości: identyfikator konta, imię '
      + 'i nazwisko, adres poczty elektronicznej, numer telefonu jeżeli został podany, numer pokoju '
      + 'i dane pobytu, treść i historię zamówień, kwoty, statusy i identyfikatory płatności oraz '
      + 'zwrotów, rodzaj użytej metody płatności wraz z marką i czterema ostatnimi cyframi karty, język '
      + 'i preferencje. Powierzenie obejmuje także dane pracowników Obiektu korzystających z panelu: '
      + 'imię i nazwisko, adres poczty elektronicznej, rolę oraz zapis czynności w systemie. HotelSpot '
      + 'nie przetwarza pełnych numerów kart płatniczych ani danych uwierzytelniających płatność. Strony '
      + 'nie przewidują powierzenia danych szczególnych kategorii. Jeżeli Obiekt umożliwi Gościom '
      + 'wprowadzanie takich danych w treści zamówienia, na przykład informacji o alergiach lub o stanie '
      + 'zdrowia przy usługach spa, odpowiada za podstawę prawną ich przetwarzania i za minimalizację zakresu.',
      '3. Polecenia administratora. HotelSpot przetwarza dane wyłącznie na udokumentowane polecenie '
      + 'Obiektu. Za takie polecenie uznaje się korzystanie z funkcji Aplikacji zgodnie z jej '
      + 'przeznaczeniem oraz ustawienia dokonane przez Obiekt w panelu. HotelSpot niezwłocznie informuje '
      + 'Obiekt, jeżeli w jego ocenie polecenie narusza przepisy o ochronie danych osobowych.',
      '4. Poufność. Do danych dopuszczane są wyłącznie osoby upoważnione przez HotelSpot, zobowiązane '
      + 'do zachowania poufności, w zakresie niezbędnym do wykonywania powierzonych obowiązków.',
      '5. Bezpieczeństwo. HotelSpot stosuje środki techniczne i organizacyjne odpowiednie do ryzyka, '
      + 'zgodnie z art. 32 RODO, w szczególności szyfrowanie transmisji, kontrolę dostępu opartą na rolach '
      + 'i regułach dostępu do bazy danych, rozdzielenie środowiska testowego i produkcyjnego, '
      + 'przechowywanie kluczy i danych uwierzytelniających w usłudze zarządzania sekretami, kopie '
      + 'zapasowe oraz rejestrowanie zdarzeń istotnych dla bezpieczeństwa.',
      '6. Dalsze powierzenie. Obiekt wyraża ogólną zgodę na korzystanie przez HotelSpot z podprocesorów. '
      + 'Są to w szczególności dostawcy infrastruktury chmurowej i bazy danych, dostawcy poczty '
      + 'elektronicznej oraz dostawcy usług tłumaczeń i funkcji opartych na modelach językowych. Aktualna '
      + 'imienna lista podprocesorów jest udostępniana w panelu oraz na żądanie Obiektu. HotelSpot zawiera '
      + 'z podprocesorami umowy zapewniające poziom ochrony nie niższy niż wynikający z niniejszego '
      + 'Załącznika i odpowiada wobec Obiektu za ich działania jak za własne. O zamiarze dodania lub '
      + 'zmiany podprocesora HotelSpot informuje z wyprzedzeniem co najmniej 30 dni. Obiekt może w tym '
      + 'terminie wnieść uzasadniony sprzeciw, a w razie braku porozumienia zaprzestać korzystania '
      + 'z funkcji, której zmiana dotyczy.',
      '7. Prawa osób, których dane dotyczą. HotelSpot, biorąc pod uwagę charakter przetwarzania, pomaga '
      + 'Obiektowi wywiązać się z obowiązku odpowiadania na żądania osób, których dane dotyczą, '
      + 'w szczególności udostępniając w Aplikacji funkcje pozwalające na dostęp do danych, ich '
      + 'sprostowanie, usunięcie i wydanie. Żądanie skierowane bezpośrednio do HotelSpot jest przekazywane '
      + 'Obiektowi bez zbędnej zwłoki i nie jest samodzielnie realizowane.',
      '8. Naruszenia ochrony danych. HotelSpot zgłasza Obiektowi naruszenie ochrony danych osobowych bez '
      + 'zbędnej zwłoki, nie później niż w ciągu 24 godzin od jego stwierdzenia, przekazując informacje '
      + 'potrzebne do zgłoszenia naruszenia organowi nadzorczemu. HotelSpot pomaga Obiektowi w wywiązaniu '
      + 'się z obowiązków określonych w art. 32 do 36 RODO, w zakresie odpowiadającym charakterowi '
      + 'przetwarzania i informacjom, którymi dysponuje.',
      '9. Usunięcie lub zwrot danych. Po zakończeniu korzystania przez Obiekt z Aplikacji HotelSpot, '
      + 'zgodnie z wyborem Obiektu, usuwa powierzone dane albo zwraca je w powszechnie używanym formacie, '
      + 'w terminie 30 dni, chyba że dalsze przechowywanie jest wymagane przepisami prawa. Dane zawarte '
      + 'w kopiach zapasowych są usuwane wraz z upływem cyklu retencji tych kopii.',
      '10. Informacje i audyt. HotelSpot udostępnia Obiektowi informacje niezbędne do wykazania spełnienia '
      + 'obowiązków wynikających z art. 28 RODO oraz umożliwia audyt, w tym inspekcję, prowadzony przez '
      + 'Obiekt lub upoważnionego przez niego audytora. Audyt odbywa się po uzgodnieniu terminu '
      + 'z wyprzedzeniem co najmniej 14 dni, nie częściej niż raz w roku kalendarzowym, w godzinach pracy '
      + 'i w sposób nienaruszający ciągłości świadczenia usług ani poufności danych innych klientów '
      + 'HotelSpot. Koszty audytu ponosi Obiekt, chyba że audyt wykaże istotne naruszenie po stronie HotelSpot.',
      '11. Przekazywanie poza Europejski Obszar Gospodarczy. Dane są przetwarzane co do zasady na terenie '
      + 'Europejskiego Obszaru Gospodarczego. Jeżeli podprocesor przetwarza dane poza tym obszarem, '
      + 'przekazanie następuje na podstawie decyzji stwierdzającej odpowiedni stopień ochrony albo '
      + 'standardowych klauzul umownych, wraz z oceną skutków przekazania.',
      '12. Odpowiedzialność. Odpowiedzialność HotelSpot z tytułu niniejszego Załącznika podlega '
      + 'ograniczeniom określonym w § 9 Regulaminu, w zakresie dozwolonym przez przepisy o ochronie '
      + 'danych osobowych.',
    ],
  },
];

interface StripeTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Odpalane po zaznaczeniu zgody. Zapis robi rodzic, bo to on zna wynik żądania. */
  onAccept: () => void;
  isSaving: boolean;
  /** Wersja regulaminu z backendu, pokazywana pod treścią (ślad, na co była zgoda). */
  termsVersion?: string;
  /** Hotel akceptował starszą wersję i musi potwierdzić nową. */
  isRenewal?: boolean;
}

/**
 * Zgoda na regulamin płatności online. Bez niej backend nie włączy płatności, więc ten
 * modal jest jedyną drogą do integracji ze Stripe'em, a nie ekranem informacyjnym.
 *
 * PRZYCISK JEST NIEAKTYWNY DO ZAZNACZENIA CHECKBOXA. Zgoda ląduje w bazie z imieniem
 * i datą osoby, która ją kliknęła, więc musi być świadomym ruchem, a nie skutkiem
 * odruchowego „dalej".
 */
export const StripeTermsModal: FC<StripeTermsModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  isSaving,
  termsVersion,
  isRenewal,
}) => {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  // Pobieramy dopiero przy otwarciu: dokument jest długi, a hotel ogląda go rzadko.
  const { data: published, isLoading: isLoadingTerms } = useGetStripeTerms(isOpen);

  /* Opublikowana wersja ma pierwszeństwo nad wbudowaną. Gdy backend nie odpowie, pokazujemy
     dokument z kodu zamiast pustej ramki: zgoda i tak zapisze się z wersją, którą backend
     uzna za obowiązującą, a modal bez treści byłby zgodą na nic. */
  const sections = published?.sections?.length ? published.sections : TERMS_DOCUMENT;

  // Zamknięcie modala kasuje zgodę: przy kolejnym otwarciu człowiek ma zaznaczyć ją
  // od nowa, a nie zastać zaznaczoną z poprzedniego podejścia.
  useEffect(() => {
    if (!isOpen) setAccepted(false);
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span className="text-[16px] font-semibold text-gray-900 dark:text-gray-100">
            {t('settings.integrations.stripe.terms.title', 'Regulamin płatności online')}
          </span>
          <span className="text-[13px] font-normal text-gray-500 dark:text-gray-400">
            {isRenewal
              ? t(
                'settings.integrations.stripe.terms.subtitleRenewal',
                'Warunki się zmieniły. Zaakceptuj nową wersję, aby dalej korzystać z płatności online.',
              )
              : t(
                'settings.integrations.stripe.terms.subtitle',
                'Zanim włączysz płatności kartą w aplikacji gościa, zapoznaj się z warunkami.',
              )}
          </span>
        </ModalHeader>

        <ModalBody>
          {/* Dokument jest długi z założenia: razem z Załącznikiem nr 1 stanowi umowę
              powierzenia, więc musi być w całości widoczny przed kliknięciem zgody. */}
          <div
            className="max-h-[55vh] overflow-y-auto rounded-[10px] border border-gray-200
              dark:border-[#374151] bg-gray-50 dark:bg-[rgba(255,255,255,0.02)] p-4 space-y-4"
          >
            {isLoadingTerms && (
              <p className="text-[13px] text-gray-500 dark:text-gray-400">
                {t('common.loading', 'Ładowanie...')}
              </p>
            )}
            {sections.map((section) => (
              <section key={section.title} className="space-y-2">
                <h4 className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                  {section.title}
                </h4>
                {(section.paragraphs || []).map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 40)}
                    className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
            {termsVersion && (
              <p className="text-[12px] text-gray-400 dark:text-gray-500">
                {t('settings.integrations.stripe.terms.version', 'Wersja regulaminu: {{version}}', { version: termsVersion })}
              </p>
            )}
          </div>

          <Checkbox
            isSelected={accepted}
            onValueChange={setAccepted}
            size="sm"
            className="mt-1"
          >
            <span className="text-[13px] text-gray-700 dark:text-gray-200">
              {t(
                'settings.integrations.stripe.terms.checkbox',
                'Zapoznałem się z regulaminem płatności online wraz z załącznikiem nr 1 '
                + '(umowa powierzenia przetwarzania danych osobowych) i akceptuję je w imieniu obiektu.',
              )}
            </span>
          </Checkbox>

          <p className="text-[12px] text-gray-500 dark:text-gray-400">
            {t(
              'settings.integrations.stripe.terms.auditNote',
              'Zapiszemy, kto i kiedy zaakceptował te warunki.',
            )}
          </p>
        </ModalBody>

        <ModalFooter>
          <Button variant="bordered" size="sm" onPress={onClose} isDisabled={isSaving}>
            {t('common.cancel', 'Anuluj')}
          </Button>
          <Button
            size="sm"
            isDisabled={!accepted}
            isLoading={isSaving}
            onPress={onAccept}
            className="bg-[#635BFF] text-white hover:bg-[#5449e0]"
          >
            {t('settings.integrations.stripe.terms.accept', 'Akceptuję i włączam płatności')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
